import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.hichatConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'first.reply.created': this.onFirstReplyCreated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'account.cache_invalidated': this.onCacheInvalidate,
      'message.reaction_updated': this.onMessageReactionUpdated,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  isAValidEvent = data => {
    return this.app.$store.getters.getCurrentAccountId === data.account_id;
  };

  onMessageUpdated = data => {
    this.app.$store.dispatch('updateMessage', data);
  };

  onPresenceUpdate = data => {
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.fetchConversationStats();
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
    this.fetchConversationStats();
  };

  onConversationRead = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    const {
      conversation: { last_activity_at: lastActivityAt },
      conversation_id: conversationId,
      message_type: messageType,
    } = data;

    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    this.app.$store.dispatch('updateConversationLastActivity', {
      lastActivityAt,
      conversationId,
    });

    // Nếu có chat message mới (không phải activity), ẩn dòng reaction ngay lập tức
    // message_type: 2 là activity theo codebase hiện tại
    if (typeof messageType !== 'undefined' && messageType !== 2) {
      this.app.$store.dispatch('updateConversation', {
        id: conversationId,
        latestReactionLine: '',
        latest_reaction_line: null,
        latestReaction: null,
        latest_reaction: null,
        reactionHoverText: '',
        reaction_hover_text: '',
      });
    }
  };

  onMessageReactionUpdated = data => {
    // data: { account_id, conversation_id, message_id, reactions_summary, reactor_id, reactor_type, emoji, conversation?, reactionHoverText?, latestReactionLine? }
    const currentUserId = this.app.$store.getters.getCurrentUserID;
    const isSelf = data.reactor_type === 'User' && data.reactor_id === currentUserId;

    // 1) Update message reactions state (and my_reaction for self)
    const payload = {
      ...data,
      ...(isSelf ? { my_reaction: data.emoji || null } : {}),
    };
    this.app.$store.commit('UPDATE_MESSAGE_REACTIONS', payload);

    // 2) Update conversation list preview and timestamp if backend sent conversation payload
    if (data.conversation && data.conversation.id) {
      this.app.$store.dispatch('updateConversation', {
        id: data.conversation.id,
        timestamp: data.conversation.timestamp,
        lastNonActivityMessage: data.conversation.lastNonActivityMessage,
        last_non_activity_message: data.conversation.last_non_activity_message,
        assignee_id: data.conversation.assignee_id,
        reactionHoverText: data.reactionHoverText || data.reaction_hover_text || '',
        reaction_hover_text: data.reaction_hover_text || data.reactionHoverText || '',
        latestReactionLine: data.latestReactionLine || data.latest_reaction_line || '',
        latest_reaction_line: data.latest_reaction_line || data.latestReactionLine || '',
        latestReaction: data.latestReaction || data.latest_reaction || null,
        latest_reaction: data.latest_reaction || data.latestReaction || null,
      });

      // bump last activity for sorting
      this.app.$store.dispatch('updateConversationLastActivity', {
        conversationId: data.conversation.id,
        lastActivityAt: data.conversation.timestamp,
      });
    } else {
      // Fallback: update by conversation_id with top-level fields
      const nowTs = Math.floor(Date.now() / 1000);
      this.app.$store.dispatch('updateConversation', {
        id: data.conversation_id,
        timestamp: nowTs,
        reactionHoverText: data.reactionHoverText || data.reaction_hover_text || '',
        reaction_hover_text: data.reaction_hover_text || data.reactionHoverText || '',
        latestReactionLine: data.latestReactionLine || data.latest_reaction_line || '',
        latest_reaction_line: data.latest_reaction_line || data.latestReactionLine || '',
        latestReaction: data.latestReaction || data.latest_reaction || null,
        latest_reaction: data.latest_reaction || data.latestReaction || null,
      });
      this.app.$store.dispatch('updateConversationLastActivity', {
        conversationId: data.conversation_id,
        lastActivityAt: nowTs,
      });
    }

    // 3) Play audio alert similar to a new incoming/private message
    const pseudoMessage = {
      message_type: 0,
      private: false,
      sender: { id: data.reactor_id },
      conversation: { assignee_id: data.conversation?.assignee_id },
    };
    DashboardAudioNotificationHelper.onNewMessage(pseudoMessage);
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => window.location.reload();

  onStatusChange = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onConversationUpdated = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  // eslint-disable-next-line class-methods-use-this
  fetchConversationStats = () => {
    emitter.emit('fetch_conversation_stats');
    emitter.emit('fetch_overview_reports');
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onFirstReplyCreated = () => {
    emitter.emit('fetch_overview_reports');
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
  };
}

export default {
  init(store, pubsubToken) {
    return new ActionCableConnector({ $store: store }, pubsubToken);
  },
};
